import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Send, Plus, Users, UserPlus } from "lucide-react";
import BlockEditor, { blocksToHtml } from "@/components/BlockEditor";

const startingBlocks = () => [
  { id: crypto.randomUUID(), type: "heading", props: { text: "Novetats de Shopping Salou", align: "left" } },
  { id: crypto.randomUUID(), type: "text", props: { text: "Hola,\n\nT'expliquem les últimes novetats de l'associació." } },
  { id: crypto.randomUUID(), type: "button", props: { text: "Descobreix-ho", url: "https://www.shoppingsalou.com" } },
];

const emptyForm = () => ({ subject: "", segment: { target_type: "associates" }, scheduled_at: "", blocks: startingBlocks() });

export default function AdminMailings() {
  const [items, setItems] = useState([]);
  const [interests, setInterests] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [previewOpen, setPreviewOpen] = useState(null);
  const [preview, setPreview] = useState({ count: 0, target_type: "" });

  const load = async () => setItems((await api.get("/mailings")).data);
  useEffect(() => { load(); api.get("/interests?active_only=true").then(({ data }) => setInterests(data)); }, []);

  // Live preview count when segment changes
  useEffect(() => {
    if (!open) return;
    const t = setTimeout(async () => {
      try {
        const { data } = await api.post("/mailings/segment/preview", form.segment);
        setPreview(data);
      } catch { setPreview({ count: 0 }); }
    }, 250);
    return () => clearTimeout(t);
  }, [form.segment, open]);

  const toggleInterest = (slug) => {
    const cur = form.segment.interests || [];
    const next = cur.includes(slug) ? cur.filter(s => s !== slug) : [...cur, slug];
    setForm({ ...form, segment: { ...form.segment, interests: next } });
  };

  const create = async () => {
    if (!form.subject.trim()) return toast.error("Cal un assumpte");
    if (!form.blocks.length) return toast.error("Afegeix almenys un bloc");
    try {
      await api.post("/mailings", {
        subject: form.subject,
        html_content: blocksToHtml(form.blocks),
        segment: form.segment,
        scheduled_at: form.scheduled_at ? new Date(form.scheduled_at).toISOString() : null,
      });
      toast.success(form.scheduled_at ? "Programat correctament" : "Guardat com esborrany");
      setOpen(false); setForm(emptyForm()); load();
    } catch { toast.error("Error"); }
  };
  const send = async (id) => {
    if (!window.confirm("Enviar aquest mailing?")) return;
    try {
      const { data } = await api.post(`/mailings/${id}/send`);
      toast.success(`Enviat a ${data.sent}/${data.total} destinataris (${data.target_type})`);
      load();
    } catch { toast.error("Error enviant"); }
  };

  return (
    <div className="space-y-6" data-testid="admin-mailings">
      <div className="flex items-end justify-between">
        <div>
          <div className="overline text-accent">Comunicació</div>
          <h1 className="mt-2 text-3xl font-heading font-bold tracking-tight">Newsletters</h1>
          <p className="text-sm text-muted-foreground mt-1">Compon amb blocs visuals. Envia a associats o a ciutadans per interès.</p>
        </div>
        <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setForm(emptyForm()); }}>
          <DialogTrigger asChild><Button className="rounded-full" data-testid="mailing-new-btn"><Plus className="w-4 h-4 mr-2" /> Nou mailing</Button></DialogTrigger>
          <DialogContent className="max-w-5xl">
            <DialogHeader><DialogTitle>Nou mailing</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div className="grid sm:grid-cols-3 gap-3">
                <div><Label>Assumpte</Label><Input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} data-testid="mailing-form-subject" /></div>
                <div>
                  <Label>Destinatari</Label>
                  <Select value={form.segment.target_type || "associates"} onValueChange={(v) => setForm({ ...form, segment: { target_type: v } })}>
                    <SelectTrigger data-testid="mailing-target-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="associates"><Users className="w-3 h-3 inline mr-2" />Associats</SelectItem>
                      <SelectItem value="citizens"><UserPlus className="w-3 h-3 inline mr-2" />Ciutadans</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Programar (opcional)</Label>
                  <Input type="datetime-local" value={form.scheduled_at} onChange={(e) => setForm({ ...form, scheduled_at: e.target.value })} data-testid="mailing-form-schedule" />
                </div>
              </div>

              {form.segment.target_type === "citizens" ? (
                <div className="border-2 border-border rounded-2xl p-4 space-y-3 bg-secondary/40">
                  <div className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">Segmentació ciutadans</div>
                  <div>
                    <Label>Interessos (deixa buit = tots amb consentiment)</Label>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-2">
                      {interests.map(i => (
                        <button key={i.slug} type="button" onClick={() => toggleInterest(i.slug)} data-testid={`mailing-interest-${i.slug}`}
                          className={`text-xs px-3 py-2 rounded-full border-2 transition-colors ${
                            (form.segment.interests || []).includes(i.slug) ? "border-accent bg-accent/10 text-primary" : "border-border"
                          }`}>{i.name_ca}</button>
                      ))}
                    </div>
                  </div>
                  <div><Label>Idioma (opcional)</Label>
                    <Select value={form.segment.language || "any"} onValueChange={(v) => setForm({ ...form, segment: { ...form.segment, language: v === "any" ? null : v } })}>
                      <SelectTrigger className="w-40" data-testid="mailing-lang-filter"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="any">Qualsevol</SelectItem>
                        <SelectItem value="ca">Català</SelectItem><SelectItem value="es">Español</SelectItem>
                        <SelectItem value="en">English</SelectItem><SelectItem value="fr">Français</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              ) : (
                <div className="border-2 border-border rounded-2xl p-4 bg-secondary/40">
                  <Label>Segmentació associats (categoria)</Label>
                  <Input className="mt-2 max-w-sm" placeholder="p.ex. restauracio (opcional)" value={form.segment.category || ""} onChange={(e) => setForm({ ...form, segment: { ...form.segment, category: e.target.value || undefined } })} data-testid="mailing-category-filter" />
                </div>
              )}

              <div className="bg-primary/5 border-l-4 border-primary rounded-lg px-4 py-2 text-sm" data-testid="mailing-preview-count">
                📊 Aquest mailing s'enviarà aproximadament a <strong>{preview.count}</strong> {preview.target_type === "citizens" ? "ciutadans" : "associats"}.
              </div>

              <BlockEditor blocks={form.blocks} onChange={(bs) => setForm({ ...form, blocks: bs })} />
            </div>
            <DialogFooter><Button onClick={create} data-testid="mailing-form-save">{form.scheduled_at ? "Programar" : "Guardar esborrany"}</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="rounded-2xl border-2 overflow-hidden">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow><TableHead>Assumpte</TableHead><TableHead>Estat</TableHead><TableHead>Enviats</TableHead><TableHead>Oberts</TableHead><TableHead>Data</TableHead><TableHead></TableHead></TableRow>
            </TableHeader>
            <TableBody>
              {items.map((m) => {
                const openRate = m.sent_count > 0 ? Math.round(((m.open_count || 0) / m.sent_count) * 100) : 0;
                return (
                <TableRow key={m.id} data-testid={`mailing-row-${m.id}`}>
                  <TableCell className="font-medium">{m.subject}</TableCell>
                  <TableCell><span className={`text-xs px-2 py-1 rounded-full ${m.status === "sent" ? "bg-emerald-100 text-emerald-700" : m.status === "scheduled" ? "bg-blue-100 text-blue-700" : "bg-secondary"}`}>{m.status}{m.status === "scheduled" && m.scheduled_at ? ` · ${new Date(m.scheduled_at).toLocaleString()}` : ""}</span></TableCell>
                  <TableCell data-testid={`mailing-sent-${m.id}`}>{m.sent_count || 0}</TableCell>
                  <TableCell data-testid={`mailing-opens-${m.id}`}>
                    <span className="font-semibold">{m.open_count || 0}</span>
                    {m.sent_count > 0 && <span className="ml-1 text-xs text-muted-foreground">({openRate}%)</span>}
                  </TableCell>
                  <TableCell className="text-xs">{new Date(m.created_at).toLocaleDateString()}</TableCell>
                  <TableCell className="flex gap-2">
                    <Button size="sm" variant="outline" className="rounded-full" onClick={() => setPreviewOpen(m)} data-testid={`mailing-preview-${m.id}`}>Vista prèvia</Button>
                    {m.status !== "sent" && (
                      <Button size="sm" onClick={() => send(m.id)} className="rounded-full" data-testid={`mailing-send-${m.id}`}><Send className="w-3.5 h-3.5 mr-1" /> Enviar</Button>
                    )}
                  </TableCell>
                </TableRow>
              );})}
              {items.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">Sense mailings.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={!!previewOpen} onOpenChange={(o) => !o && setPreviewOpen(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader><DialogTitle>{previewOpen?.subject}</DialogTitle></DialogHeader>
          <div className="rounded-xl overflow-hidden max-h-[70vh] overflow-y-auto border-2">
            <div dangerouslySetInnerHTML={{ __html: previewOpen?.html_content || "" }} />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
